import React, { useState } from 'react';
import { 
  FileText, 
  User,
  Truck,
  Shield,
  CheckCircle,
  Download,
  Eye,
  Edit,
  Trash2,
  CheckSquare,
  Square,
  MoreVertical,
  Calendar,
  Tag,
  ChevronDown,
  ChevronUp,
  Info,
  FileCheck,
  Upload as UploadIcon,
  Clock as ClockIcon
} from 'lucide-react';
import { format } from 'date-fns';
import MobileGestureHandler from './MobileGestureHandler';

const MobileDocumentCard = ({ 
  document, 
  isSelected, 
  onSelect, 
  onAction,
  showCheckbox = true 
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'license':
      case 'endorsement':
        return Shield;
      case 'registration':
      case 'inspection':
        return Truck;
      case 'medical':
      case 'drug_test':
        return User;
      default:
        return FileText;
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'driver':
        return User;
      case 'vehicle':
        return Truck;
      case 'company':
        return Shield;
      default:
        return FileText;
    }
  };

  const formatCountdown = (daysLeft) => {
    if (typeof daysLeft !== 'number' || Number.isNaN(daysLeft)) {
      return 'No expiration';
    }
    if (daysLeft < 0) {
      return `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? '' : 's'} ago`;
    }
    if (daysLeft === 0) return 'Expires today';
    if (daysLeft === 1) return 'Expires tomorrow';
    if (daysLeft <= 7) return `${daysLeft} days left`;
    if (daysLeft <= 30) return `${daysLeft} days left`;
    return `${Math.ceil(daysLeft / 30)} months left`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return '—';
    }
  };

  const TypeIcon = getTypeIcon(document.type);
  const EntityIcon = getEntityIcon(document.entityType);
  const StatusIcon = document.icon || CheckCircle;

  const getUrgencyStyles = () => {
    if (document.status === 'expired') {
      return {
        border: 'border-l-red-500',
        bg: 'bg-red-50',
        statusBg: 'bg-red-100 text-red-800 border-red-200'
      };
    }
    if (document.status === 'expiring_soon') {
      return {
        border: 'border-l-amber-500',
        bg: 'bg-amber-50',
        statusBg: 'bg-amber-100 text-amber-800 border-amber-200'
      };
    }
    return {
      border: 'border-l-gray-200',
      bg: 'bg-white',
      statusBg: 'bg-green-100 text-green-800 border-green-200'
    };
  };

  const urgencyStyles = getUrgencyStyles();

  return (
    <MobileGestureHandler
      onSwipeLeft={() => onAction('delete', document)}
      onSwipeRight={() => onAction('download', document)}
      onLongPress={() => onSelect(document.id)}
      className={`${urgencyStyles.bg} border border-gray-200 ${urgencyStyles.border} border-l-4 rounded-lg shadow-sm mb-3 overflow-hidden transition-transform duration-200 active:scale-95`}
    >
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            {showCheckbox && (
              <button
                onClick={() => onSelect(document.id)}
                className="mt-1 flex-shrink-0"
              >
                {isSelected ? (
                  <CheckSquare className="h-5 w-5 text-blue-600" />
                ) : (
                  <Square className="h-5 w-5 text-gray-400" />
                )}
              </button>
            )}
            
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TypeIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {document.title}
              </h3>
              <div className="flex items-center space-x-1 mt-1">
                <EntityIcon className="h-3 w-3 text-gray-400" />
                <p className="text-xs text-gray-500 truncate">
                  {document.entityName}
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <MoreVertical className="h-5 w-5" />
          </button>
        </div>

        {/* Status and Type */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${urgencyStyles.statusBg}`}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {document.status.replace(/_/g, ' ')}
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
              <Tag className="h-3 w-3 mr-1" />
              {document.type.replace(/_/g, ' ')}
            </span>
          </div>
        </div>

        {/* Expiration Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-xs text-gray-500">
              Exp: {formatDate(document.expirationDate)}
            </span>
          </div>
          <div className={`text-xs font-medium ${
            typeof document.daysLeft === 'number'
              ? (document.daysLeft < 0
                ? 'text-red-600'
                : document.daysLeft <= 30
                  ? 'text-amber-600'
                  : 'text-green-600')
              : 'text-gray-500'
          }`}>
            {formatCountdown(document.daysLeft)}
          </div>
        </div>

        {/* Document Number and Details Toggle */}
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between">
            {document.documentNumber && (
              <p className="text-xs text-gray-500">
                #{document.documentNumber}
              </p>
            )}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <Info className="h-4 w-4" />
              <span>{showDetails ? 'Hide Details' : 'Show Details'}</span>
              {showDetails ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Detailed Information Panel */}
      {showDetails && (
        <div className="bg-blue-50 border-t border-blue-100 px-4 py-3">
          <div className="grid grid-cols-1 gap-3">
            {/* Applies To Section */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <EntityIcon className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Applies To</span>
              </div>
              <div className="ml-6 space-y-1">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{document.entityName}</span>
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {document.entityType} • {document.category}
                </p>
                {document.entityId && (
                  <p className="text-xs text-gray-500">
                    ID: {document.entityId}
                  </p>
                )}
              </div>
            </div>

            {/* Document Information */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <FileCheck className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Document Info</span>
              </div>
              <div className="ml-6 space-y-1">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Type:</span>
                    <p className="font-medium text-gray-700 capitalize">
                      {document.type.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Category:</span>
                    <p className="font-medium text-gray-700 capitalize">
                      {document.category}
                    </p>
                  </div>
                </div>
                {document.issuingAuthority && (
                  <div>
                    <span className="text-xs text-gray-500">Issued by:</span>
                    <p className="text-sm font-medium text-gray-700">
                      {document.issuingAuthority}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Important Dates */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Important Dates</span>
              </div>
              <div className="ml-6 space-y-1">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {document.issueDate && (
                    <div>
                      <span className="text-gray-500">Issued:</span>
                      <p className="font-medium text-gray-700">
                        {formatDate(document.issueDate)}
                      </p>
                    </div>
                  )}
                  {document.expirationDate && (
                    <div>
                      <span className="text-gray-500">Expires:</span>
                      <p className={`font-medium ${
                        typeof document.daysLeft === 'number'
                          ? (document.daysLeft < 0
                            ? 'text-red-600'
                            : document.daysLeft <= 30
                              ? 'text-amber-600'
                              : 'text-green-600')
                          : 'text-gray-700'
                      }`}>
                        {formatDate(document.expirationDate)}
                      </p>
                    </div>
                  )}
                </div>
                {typeof document.daysLeft === 'number' && (
                  <div className="mt-2 p-2 bg-white rounded border">
                    <div className="flex items-center space-x-2">
                      <ClockIcon className={`h-3 w-3 ${
                        document.daysLeft < 0 ? 'text-red-500' :
                        document.daysLeft <= 30 ? 'text-amber-500' : 'text-green-500'
                      }`} />
                      <span className={`text-xs font-medium ${
                        document.daysLeft < 0 ? 'text-red-600' :
                        document.daysLeft <= 30 ? 'text-amber-600' : 'text-green-600'
                      }`}>
                        {formatCountdown(document.daysLeft)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* File Information */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <UploadIcon className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">File Details</span>
              </div>
              <div className="ml-6 space-y-1">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {document.fileName && (
                    <div className="col-span-2">
                      <span className="text-gray-500">File:</span>
                      <p className="font-medium text-gray-700 truncate">
                        {document.fileName}
                      </p>
                    </div>
                  )}
                  {document.fileSize && (
                    <div>
                      <span className="text-gray-500">Size:</span>
                      <p className="font-medium text-gray-700">
                        {document.fileSize > 1024 * 1024 
                          ? `${Math.round(document.fileSize / (1024 * 1024))} MB`
                          : `${Math.round(document.fileSize / 1024)} KB`
                        }
                      </p>
                    </div>
                  )}
                  {document.mimeType && (
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <p className="font-medium text-gray-700">
                        {document.mimeType.split('/')[1]?.toUpperCase() || 'Unknown'}
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-xs text-gray-500">Uploaded by:</span>
                  <p className="text-sm font-medium text-gray-700">
                    {document.uploadedBy}
                  </p>
                  {document.uploadDate && (
                    <p className="text-xs text-gray-500">
                      on {formatDate(document.uploadDate)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Tags */}
            {document.tags && document.tags.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Tag className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Tags</span>
                </div>
                <div className="ml-6">
                  <div className="flex flex-wrap gap-1">
                    {document.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions in Details */}
            <div className="pt-2 border-t border-blue-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-blue-900">Quick Actions</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => onAction('view', document)}
                    className="p-2 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    title="View Document"
                  >
                    <Eye className="h-4 w-4 text-blue-600" />
                  </button>
                  <button
                    onClick={() => onAction('download', document)}
                    className="p-2 bg-white border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
                    title="Download Document"
                  >
                    <Download className="h-4 w-4 text-green-600" />
                  </button>
                  <button
                    onClick={() => onAction('edit', document)}
                    className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    title="Edit Document"
                  >
                    <Edit className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-gray-50 px-4 py-3 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => onAction('view', document)}
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <Eye className="h-4 w-4" />
              <span className="text-xs font-medium">View</span>
            </button>
            <button
              onClick={() => onAction('download', document)}
              className="flex items-center space-x-1 text-green-600 hover:text-green-800 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span className="text-xs font-medium">Download</span>
            </button>
          </div>
          
          <div className="text-xs text-gray-400">
            {document.uploadedBy || 'System'}
          </div>
        </div>
      </div>

      {/* Expanded Actions Menu */}
      {showActions && (
        <div className="bg-white border-t border-gray-200 p-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                onAction('edit', document);
                setShowActions(false);
              }}
              className="flex items-center justify-center space-x-2 p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
            >
              <Edit className="h-4 w-4" />
              <span>Edit</span>
            </button>
            <button
              onClick={() => {
                onAction('delete', document);
                setShowActions(false);
              }}
              className="flex items-center justify-center space-x-2 p-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      )}
    </MobileGestureHandler>
  );
};

export default MobileDocumentCard;